import { update } from 'react-querybuilder';
import CustomAutocomplete from 'components/Inputs/CustomAutocomplete';
import { autocompleteSxProps } from './utilities';
import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import LoaderComponent from 'components/Loader';

const AsyncAutoComplete = ({
	query,
	setQuery,
	path,
	fields,
	rule,
	fieldValueOptionsCallback,
	isProjectDetails,
	sx,
	customTextFieldProps,
	componentsProps
}: any) => {
	const defaultOptions: any[] =
		fields[rule.field]?.fieldOptions?.options?.map((item: any) => ({
			value: item,
			label: item
		})) ?? [];

	const [loading, setLoading] = useState(false);
	const [inputValue, setInputValue] = useState('');
	const [fetchedOptions, setFetchedOptions] = useState<any[]>(defaultOptions);
	const [primaryOptions, setPrimaryOptions] = useState<any[]>([]);
	const isFetchOptionEnabled = !!fieldValueOptionsCallback && inputValue.length > 2;

	const fetchOptions = async (query: string): Promise<any[]> => {
		const fieldsRes = await fieldValueOptionsCallback({ searchField: rule.field, searchText: query });
		if (fieldsRes.success) {
			const options = fieldsRes.data.result.filter(Boolean).map((item: any) => ({
				value: item,
				label: item
			}));
			return options;
		}
		return [];
	};

	useEffect(() => {
		if (inputValue.length < 3) {
			if (rule.field !== '') {
				if (rule.operator === 'null' || rule.operator === 'notNull') {
					setFetchedOptions([]);
					setPrimaryOptions([]);
				} else setFetchedOptions(primaryOptions);
			}
		}
	}, [inputValue]);

	useEffect(() => {
		if (rule.field !== '') {
			setLoading(true);
			fetchOptions(inputValue)
				.then((options) => {
					setFetchedOptions(options);
					setPrimaryOptions(options);
				})
				.finally(() => setLoading(false));
		}
	}, []);

	useEffect(() => {
		if (rule.field !== '') {
			setLoading(true);
			fetchOptions(inputValue)
				.then((options) => {
					setFetchedOptions(options);
					setPrimaryOptions(options);
				})
				.finally(() => setLoading(false));
		}
	}, [rule.field]);

	useEffect(() => {
		if (rule.field !== '') {
			if (rule.operator === 'null' || rule.operator === 'notNull') {
				setFetchedOptions([]);
				setPrimaryOptions([]);
			} else {
				if (fetchedOptions.length === 0 || primaryOptions.length === 0) {
					setLoading(true);
					fetchOptions(inputValue)
						.then((options) => {
							setFetchedOptions(options);
							setPrimaryOptions(options);
						})
						.finally(() => setLoading(false));
				}
			}
		} else {
			setFetchedOptions([]);
			setPrimaryOptions([]);
		}
	}, [rule.operator]);

	useEffect(() => {
		if (isFetchOptionEnabled) {
			setLoading(true);
			fetchOptions(inputValue)
				.then((options) => setFetchedOptions(options))
				.finally(() => setLoading(false));
		} else {
			setFetchedOptions(primaryOptions);
		}
	}, [inputValue]);

	return (
		<CustomAutocomplete
			freeSolo
			noOptionsText="No data found"
			disabled={rule.operator === 'null' || rule.operator === 'notNull'}
			{...(isFetchOptionEnabled && { filterOptions: (x) => x })}
			onInputChange={(_e, value, reason) => {
				if (!rule.field) {
					return;
				}

				if (reason === 'input' || reason === 'clear') {
					setInputValue(value);
					setQuery(update(query, 'value', value, path));
				}
			}}
			onChange={(_e, val) => {
				if (!rule.field) {
					return;
				}

				const newValue = val?.value ?? '';
				setQuery(update(query, 'value', newValue, path));
			}}
			options={loading ? [''] : fetchedOptions}
			value={rule.value}
			sx={{
				...autocompleteSxProps,
				...(isProjectDetails && {
					background: '#EFF6FF !important',
					borderRadius: '4px !important',
					'& .MuiOutlinedInput-root': {
						borderRadius: '4px !important',
					},
					'& .MuiOutlinedInput-notchedOutline': {
						borderRadius: '4px !important',
					},
					'& .MuiInputBase-input': {
						color: '#000000 !important',
						fontWeight: '500 !important',
					},
				}),
				...sx,
			}}
			customTextFieldProps={{
				...customTextFieldProps,
				sx: {
					'& .MuiInputBase-root': {
						height: '32px !important',
					},
					...customTextFieldProps?.sx,
				},
			}}
			componentsProps={componentsProps}
			renderOption={(props, option) => {
				const { key, ...optionProps } = props;

				return (
					<Box
						component="li"
						key={key}
						{...optionProps}
					>
						{loading ? (
							<LoaderComponent size={25} />
						) : (
							<Typography>
								{option?.label ?? ''}
							</Typography>
						)}
					</Box>
				);
			}}
		/>
	);
};

export default AsyncAutoComplete;
